import React, { useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Image,
    Link,
    ListItem,
    Text,
    UnorderedList,
    useBreakpointValue,
    useTheme
} from '@chakra-ui/react';
import { css } from '@emotion/react';
import {
    formatDateWithTimezone,
    getContractStatusTranslation,
    getInvoiceStatusTranslation
} from 'ts/common/common';
import ClientPageCardHeader from 'ts/common/components/ClientPageCardHeader';
import { ContractContext } from 'ts/common/contract/types';
import { needsToSign } from 'ts/common/contract/utils';
import { useFillableFields, useTranslate } from 'ts/common/hooks';
import { UseContractUserType } from 'ts/common/hooks/contract/useContractUserType';
import { usePrintOrDownloadContract } from 'ts/common/hooks/contract/usePrintOrDownloadContract';
import ModernButton from 'ts/studio/common/ModernButton';
import ContractActionTag from './ContractActionTag';
import ContractSignature from './ContractSignature';

const fillableFieldStyle = (theme) => css`
    p {
        margin: 1em 0;
    }

    ol,
    ul {
        margin: 1em;
        padding: revert;
    }

    .fillable-field-short {
        background: inherit;
        border: none;
        border-bottom: 1px dashed ${theme.colors.gray[400]};
        color: ${theme.colors.text};
        height: 30px;
        min-width: 100px;
        outline: none;

        &:focus {
            background-color: ${theme.colors.gray[200]};
            color: black;
        }

        /* Remove Chrome's autocomplete styles */
        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px white inset !important;
        }

        &::placeholder {
            color: ${theme.isLightColorScheme ? theme.colors.gray[400] : theme.colors.gray[200]};
            font-style: italic;
            opacity: 0.7;
        }
    }

    .fillable-field-long {
        background: inherit;
        border-color: ${theme.colors.gray[300]};
        color: ${theme.colors.text};
        min-height: 140px;
        width: 100%;

        &:focus {
            background-color: ${theme.colors.gray[200]};
            color: black;
        }

        &::placeholder {
            color: ${theme.isLightColorScheme ? theme.colors.gray[400] : theme.colors.gray[200]};
            font-style: italic;
            opacity: 0.7;
        }
    }

    .text-center {
        text-align: center;
    }
`;

const MAX_LOGO_WIDTH = 400;

const getLogoHeight = (height: number, width: number) => {
    const aspectRatio = width / height;

    if (width > MAX_LOGO_WIDTH) {
        return MAX_LOGO_WIDTH / aspectRatio;
    }

    return height;
};

interface ContractProps {
    brandName: string;
    contractData: SpApi.IBaseContract;
    contractUserEmail: string;
    heartbeat?: () => void;
    openSignatureModal: () => void;
    onFillableFieldsChange?: (fields: SpApi.IContractFillableField[]) => void;
    translateKeyBase?: string;
    onStart?: () => void;
    context?: ContractContext;
}

const Contract: React.FC<ContractProps> = ({
    brandName,
    contractData,
    contractUserEmail,
    heartbeat,
    openSignatureModal,
    onFillableFieldsChange,
    translateKeyBase = 'contract',
    onStart,
    context
}) => {
    const isClientContext =
        context === ContractContext.CLIENT_BOOKING_CONTRACT ||
        context === ContractContext.CLIENT_STANDALONE_CONTRACT;
    const { isPrimaryContact, isSecondaryContact, isStudioUser, isClientUser } =
        UseContractUserType(contractUserEmail, contractData.contacts);

    const contractUserNeedsToSign = needsToSign(contractUserEmail, contractData, context);

    const theme = useTheme();
    const t = useTranslate(translateKeyBase);
    const isMobile = useBreakpointValue({ base: true, md: false });
    const { fillableFields, fillableFieldRequiringAttention, parsedContractHtml } =
        useFillableFields({
            areFillableFieldsCompleted: false,
            areFillableFieldsDisabled: isStudioUser || !contractUserNeedsToSign,
            contractHtml: contractData.bodyHtml
        });

    const contractUrl = contractData.links?.alternate?.href;
    const { printContract, downloadContract } = usePrintOrDownloadContract(contractUrl);

    const getCTAButton = (
        fillableFieldsCompletedCount: number,
        fillableFieldsTotalCount: number,
        nextEmptyField?: SpApi.IContractFillableField
    ) => {
        const ButtonComponent = isClientContext ? Button : ModernButton;

        if (isClientUser && fillableFieldsTotalCount > 0 && fillableFieldsCompletedCount === 0) {
            return (
                <ButtonComponent
                    onClick={() => {
                        heartbeat?.();
                        onStart?.();

                        if (nextEmptyField) {
                            const fieldElement = document.getElementById(nextEmptyField.id);

                            if (fieldElement) {
                                fieldElement.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                });
                                fieldElement.focus();
                            }
                        }
                    }}
                    paddingX="32px"
                    variant="primary"
                >
                    {t('start')}
                </ButtonComponent>
            );
        }

        if (contractUserNeedsToSign) {
            return (
                <ButtonComponent
                    onClick={() => {
                        heartbeat?.();
                        const signButtonElement = document.getElementById('signButton');

                        if (signButtonElement) {
                            signButtonElement.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                            signButtonElement.focus();
                        }
                    }}
                    paddingX="32px"
                    isDisabled={fillableFieldsCompletedCount < fillableFieldsTotalCount}
                    variant="primary"
                >
                    {t('sign')}
                </ButtonComponent>
            );
        }

        return (
            <ButtonComponent onClick={printContract} paddingX="32px" variant="primary">
                {t('print')}
            </ButtonComponent>
        );
    };

    useEffect(() => {
        if (onFillableFieldsChange) {
            onFillableFieldsChange(fillableFields);
        }
    }, [fillableFields, onFillableFieldsChange]);

    useEffect(() => {
        const handleFocus = (event: FocusEvent) => {
            const target = event.target as HTMLElement;

            if (
                target.classList.contains('fillable-field-short') ||
                target.classList.contains('fillable-field-long')
            ) {
                heartbeat?.();
            }
        };

        document.addEventListener('focus', handleFocus, true);

        return () => {
            document.removeEventListener('focus', handleFocus, true);
        };
    }, [heartbeat]);

    const fillableFieldsCompletedCount = fillableFields.filter(
        (field) => field.value !== ''
    ).length;
    const showSignHereActionTag =
        fillableFieldsCompletedCount === fillableFields.length && contractUserNeedsToSign;
    const shouldDisplayDueDate = context !== ContractContext.CLIENT_BOOKING_CONTRACT;

    const primaryContractSignature = contractData.signatureClients?.find(
        (signature) => signature.signerEmail === contractData.contacts[0]?.email
    );
    const secondaryContractSignature = contractData.signatureClients?.find(
        (signature) => signature.signerEmail === contractData.contacts[1]?.email
    );

    return (
        <Box maxW="1000px" mx="auto" as="main">
            <ClientPageCardHeader
                headingText={contractData.name}
                statusText={getContractStatusTranslation(t, contractData.contractStatus)}
                actionButtons={{
                    onClickPrint: printContract,
                    onClickDownload: () => {
                        heartbeat?.();
                        downloadContract();
                    },
                    printLabel: t('print'),
                    downloadLabel: t('download')
                }}
            />

            {/* Contract Info Section */}
            <Flex
                mb={10}
                pt={4}
                gap="30px"
                data-testid="contract-info-section"
                alignItems="flex-start"
                role="region"
                aria-label={t('contractDetails')}
            >
                <Flex gap="30px" flexWrap="wrap" flex="1 1 0%">
                    <Box flex="0 1 auto" minW="fit-content">
                        <Text color="neutral.700" fontSize="14px" fontWeight="600" mb={1}>
                            {t('from')}
                        </Text>
                        <Text color="neutral.600" fontSize="14px" fontWeight="400">
                            {brandName}
                        </Text>
                    </Box>
                    <Box flex="0 1 auto" minW="fit-content">
                        <Text color="neutral.700" fontSize="14px" fontWeight="600" mb={1}>
                            {t('primaryContact')}
                        </Text>
                        <Flex alignItems="center" gap={2}>
                            <Text fontSize="14px" fontWeight="400" color="neutral.600">
                                {contractData.contacts[0]?.name}
                            </Text>
                        </Flex>
                    </Box>
                    {contractData.shootDate && (
                        <Box flex="0 1 auto" minW="fit-content">
                            <Text color="neutral.700" fontSize="14px" fontWeight="600" mb={1}>
                                {t('shootDate')}
                            </Text>
                            <Text color="neutral.600" fontSize="14px" fontWeight="400">
                                {formatDateWithTimezone(
                                    contractData.shootDate,
                                    contractData.brandTimezone
                                )}
                            </Text>
                        </Box>
                    )}
                    {shouldDisplayDueDate && (
                        <Box flex="0 1 auto" minW="fit-content">
                            <Text color="neutral.700" fontSize="14px" fontWeight="600" mb={1}>
                                {t('dueDate')}
                            </Text>
                            <Text color="neutral.600" fontSize="14px" fontWeight="400">
                                {formatDateWithTimezone(
                                    contractData.expirationDate,
                                    contractData.brandTimezone
                                )}
                            </Text>
                        </Box>
                    )}
                    {contractData.invoices?.[0] && (
                        <Box
                            flex="0 1 auto"
                            minW="fit-content"
                            display="flex"
                            flexDirection="column"
                        >
                            <Text color="neutral.700" fontSize="14px" fontWeight="600" mb={1}>
                                {t('invoice')}
                            </Text>
                            <Link
                                href={contractData.invoices[0].links.portal?.href}
                                target="_blank"
                                _hover={{ textDecoration: 'none' }}
                            >
                                <Text fontSize="14px" fontWeight="400" color="brand.primary">
                                    {t('displayName', {
                                        customInvoiceId: contractData.invoices[0].customInvoiceId
                                    })}
                                    {` (${getInvoiceStatusTranslation(
                                        t,
                                        contractData.invoices[0].invoiceStatus
                                    )})`}
                                </Text>
                            </Link>
                        </Box>
                    )}
                    {fillableFields.length > 0 && (
                        <Box flex="0 1 auto" minW="fit-content">
                            <Text color="neutral.700" fontSize="14px" fontWeight="600" mb={1}>
                                {t('requiredFields')}
                            </Text>
                            <Text fontSize="14px" fontWeight="400" color="neutral.600">
                                {fillableFieldsCompletedCount} / {fillableFields.length}
                            </Text>
                        </Box>
                    )}
                </Flex>

                <Box flex="0 0 auto">
                    {getCTAButton(
                        fillableFieldsCompletedCount,
                        fillableFields.length,
                        fillableFieldRequiringAttention
                    )}
                </Box>
            </Flex>

            {/* Brand Logo Section */}
            {contractData.brandTheme?.emailBrandLogo?.displayUrl?.original && (
                <Box
                    mb="45px"
                    display="flex"
                    height={getLogoHeight(
                        contractData.brandTheme.emailBrandLogo.height,
                        contractData.brandTheme.emailBrandLogo.width
                    )}
                    justifyContent="center"
                >
                    <Image
                        src={contractData.brandTheme.emailBrandLogo.displayUrl.original}
                        alt={contractData.brandTheme.logoAltText || brandName}
                        maxW={MAX_LOGO_WIDTH}
                        objectFit="contain"
                        loading="lazy"
                    />
                </Box>
            )}

            {/* Contract Content */}
            <Box mb={12} role="region" aria-label={t('contractContent')}>
                <Box
                    dangerouslySetInnerHTML={{
                        __html: parsedContractHtml
                    }}
                    fontSize="16px"
                    fontWeight="400"
                    fontFamily="open-sans"
                    lineHeight="1.6"
                    color="neutral.600"
                    css={() => {
                        return fillableFieldStyle(theme);
                    }}
                />
            </Box>

            {/* Signatures Section */}
            <Box mt={12} role="region" aria-labelledby="signatures-heading">
                <Text
                    fontSize="18px"
                    fontWeight="700"
                    color="neutral.600"
                    mb={4}
                    id="signatures-heading"
                >
                    {t('signatures')}
                </Text>
                <ContractSignature
                    isSignable={contractUserNeedsToSign && isPrimaryContact}
                    name={contractData.contacts[0].name}
                    openSignatureModal={() => {
                        heartbeat?.();
                        openSignatureModal();
                    }}
                    signature={primaryContractSignature?.signature}
                    translateKeyBase={translateKeyBase}
                    filledAllRequiredFields={fillableFieldsCompletedCount >= fillableFields.length}
                />
                {contractData.contacts.length > 1 && (
                    <ContractSignature
                        name={contractData.contacts[1].name}
                        isSignable={contractUserNeedsToSign && isSecondaryContact}
                        openSignatureModal={openSignatureModal}
                        signature={secondaryContractSignature?.signature}
                        translateKeyBase={translateKeyBase}
                        filledAllRequiredFields={
                            fillableFieldsCompletedCount >= fillableFields.length
                        }
                    />
                )}
                <ContractSignature
                    name={brandName}
                    isSignable={contractUserNeedsToSign && isStudioUser}
                    openSignatureModal={openSignatureModal}
                    signature={contractData.signatureStudio?.signature}
                    translateKeyBase={translateKeyBase}
                    filledAllRequiredFields={fillableFieldsCompletedCount >= fillableFields.length}
                />
            </Box>

            {/* Signature Information Section */}
            {contractData.signatureClients?.map((clientSignature) => (
                <Box
                    key={clientSignature.signature.publicId}
                    mt={8}
                    bg={
                        theme.isLightColorScheme === false
                            ? theme.colors.gray[700]
                            : theme.colors.gray[200]
                    }
                    p={4}
                    borderTop="1px solid"
                    borderColor="neutral.300"
                    fontFamily="open-sans"
                >
                    <Text fontSize="12px" fontWeight="600" mb={2}>
                        {t('clientSignatureInformation')}
                    </Text>
                    <UnorderedList margin={0} spacing={1} padding="revert">
                        <ListItem fontSize="12px" color="gray.400" fontWeight={400}>
                            {t('contractId')}: {contractData.publicId}
                        </ListItem>
                        <ListItem fontSize="12px" color="gray.400" fontWeight={400}>
                            {t('signatureId')}: {clientSignature.signature.publicId}
                        </ListItem>
                        <ListItem fontSize="12px" color="gray.400" fontWeight={400}>
                            {t('timestamp')}: {clientSignature.created}
                        </ListItem>
                        <ListItem fontSize="12px" color="gray.400" fontWeight={400}>
                            {t('ipAddress')}: {clientSignature.ipAddress}
                        </ListItem>
                        <ListItem fontSize="12px" color="gray.400" fontWeight={400}>
                            {t('userAgent')}: {clientSignature.userAgent}
                        </ListItem>
                    </UnorderedList>
                </Box>
            ))}

            <Box position="absolute" right={isMobile ? '0px' : '-75px'} top="0" height="100%">
                {isClientUser && fillableFieldRequiringAttention && (
                    <ContractActionTag
                        actionText={t('fillHere')}
                        element={document.getElementById(fillableFieldRequiringAttention.id)}
                    />
                )}
                {showSignHereActionTag && (
                    <ContractActionTag
                        actionText={t('signHere')}
                        element={document.getElementById('signButton')}
                    />
                )}
            </Box>
        </Box>
    );
};

export default Contract;
